import { differenceInDays, parseISO } from 'date-fns';
import { type ReactNode, useMemo } from 'react';

import type { ApiAddress } from '#/api/types/ApiAddress';
import type { ApiRemoteOptions } from '#/api/types/ApiRemoteOptions';
import type { ApiISODateTimeString } from '#/api/types/ApiTypedDate';
import { Box } from '#/components/Box/Box';
import { Icon } from '#/components/Icon/Icon';
import { IdealistDayLabel } from '#/components/IdealistDays/IdealistDayLabel';
import { LegacyTooltip } from '#/components/LegacyTooltip/LegacyTooltip';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { useIsClient } from '#/hooks/useIsClient';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingTypeLabel } from '#/modules/listing/components/ListingTypeLabel/ListingTypeLabel';
import { useRsvpAnswer } from '#/modules/listing/events/hooks/useRsvpAnswer';
import type { ApiOrgType } from '#/modules/listing/orgs/api/types/ApiOrgType';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import { spacing } from '#/theme/theme';
import { getLocationTypeText } from '#/utils/listings/getLocationTypeText';

import {
  ListingViewHeaderContent,
  ListingViewHeaderContentHolder,
  ListingViewHeaderHolder,
  ListingViewHeaderLocationDescription,
  ListingViewHeaderOrgName,
  ListingViewHeaderTagHolder,
  ListingViewHeaderTitle,
} from './ListingViewHeader.styled';
import { ListingViewHeaderDoneInADayTag } from './ListingViewHeaderDoneInADayTag';
import { ListingViewHeaderPublishedTag } from './ListingViewHeaderPublishedTag';
import { getListingViewHeaderLocationSentence } from './getListingViewHeaderLocationSentence';

type Props = {
  address: ApiAddress;
  hasRsvp?: boolean;
  isPostedAnonymously: boolean;
  isPreview?: boolean;
  listingId: string;
  listingName: string;
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT';
  listingFirstPublished: ApiISODateTimeString | null;
  orgName: string | null;
  orgType: ApiOrgType | null;
  orgUrl: string | null;
  remoteOptions: ApiRemoteOptions;
  isIdealistDay?: boolean;
  canBeDoneInADay?: boolean;
  actionButtons: ReactNode;
  listingViewStatus: ListingViewStatus;
};

export function ListingViewHeader({
  address,
  hasRsvp,
  isPostedAnonymously,
  isPreview = false,
  listingId,
  listingName,
  listingType,
  listingFirstPublished,
  orgName,
  orgType,
  orgUrl,
  remoteOptions,
  isIdealistDay,
  canBeDoneInADay,
  actionButtons,
  listingViewStatus,
}: Props) {
  const { i18n } = useI18n();
  const isClient = useIsClient();

  // initialize rsvp buttons if this listing has rsvp enabled
  useRsvpAnswer(listingId, hasRsvp);

  const locationSentence = useMemo(
    () =>
      getListingViewHeaderLocationSentence({
        i18n,
        listingType,
        remoteOptions,
        address,
      }),
    [i18n, address, listingType, remoteOptions],
  );

  const isNewListing =
    isClient &&
    ![ListingViewStatus.EXPIRED, ListingViewStatus.PREVIEW].includes(
      listingViewStatus,
    ) &&
    Boolean(
      listingFirstPublished &&
        differenceInDays(new Date(), parseISO(listingFirstPublished)) < 2,
    );

  return (
    <ListingViewHeaderHolder>
      <ListingTypeLabel listingType={listingType} orgType={orgType} />

      <div style={{ display: 'flex', alignItems: 'center', gap: spacing[16] }}>
        {listingFirstPublished && !isPreview && (
          <ListingViewHeaderTagHolder
            style={{ minWidth: 240 }}
            $color={isNewListing ? 'lightYellow' : 'backgroundGrey'}
          >
            <ListingViewHeaderPublishedTag
              listingFirstPublished={listingFirstPublished}
              isNew={isNewListing}
            />
          </ListingViewHeaderTagHolder>
        )}

        {canBeDoneInADay && (
          <Box display="inline">
            <ListingViewHeaderTagHolder $color="lightPurple">
              <ListingViewHeaderDoneInADayTag />
            </ListingViewHeaderTagHolder>
          </Box>
        )}
      </div>

      <ListingViewHeaderContentHolder>
        <ListingViewHeaderContent>
          {isIdealistDay && <IdealistDayLabel />}

          <ListingViewHeaderTitle data-qa-id="listing-name">
            {listingName}
            {isPostedAnonymously && (
              <Box display="inline-block" ml="6px" verticalAlign="super">
                <LegacyTooltip>
                  {i18n(
                    `The hiring organization has chosen to publish this listing anonymously to maintain confidentiality during the hiring process. They have provided Idealist with all of their organization’s details and currently meet our posting guidelines.`,
                  )}
                </LegacyTooltip>
              </Box>
            )}
          </ListingViewHeaderTitle>

          {orgName && (
            <ListingViewHeaderOrgName>
              {orgUrl ? (
                <StyledNativeLinkWithRef href={orgUrl} qaId="org-link">
                  {orgName}
                </StyledNativeLinkWithRef>
              ) : (
                orgName
              )}
            </ListingViewHeaderOrgName>
          )}

          <ListingViewHeaderLocationDescription>
            <Icon name="location-filled" size={20} colorName="dustyBlue" />

            <div>
              <span>
                {getLocationTypeText(
                  i18n,
                  listingType,
                  remoteOptions.locationType,
                )}
                ,
              </span>

              <span> {locationSentence}</span>
            </div>
          </ListingViewHeaderLocationDescription>

          {actionButtons}
        </ListingViewHeaderContent>
      </ListingViewHeaderContentHolder>
    </ListingViewHeaderHolder>
  );
}
